/**
 * Assessment Configuration
 *
 * Defines different assessment types and their question mappings:
 * - FULL: Complete assessment with all questions
 * - BASIC: Minimal questions needed for macro calculation
 * - NONE: No assessment required
 * - CUSTOM: Trainer-created custom assessments (future)
 */

import {
  QUESTION_TYPES,
  CUSTOM_QUESTION_TYPE_TO_COMPONENT,
  normalizeOptionsForComponent,
} from './assessmentQuestionTypes';

export { QUESTION_TYPES };

export const ASSESSMENT_TYPES = {
  FULL: 'full',
  BASIC: 'basic',
  NONE: 'none',
  CUSTOM: 'custom', // Prefix format: 'custom:{trainerId}'
};

/** Question keys required in every assessment (for BMR/macro calculation). Match API assessmentConstants. */
export const REQUIRED_QUESTION_KEYS = [
  'age',
  'weight',
  'height',
  'gender',
  'activityLevel',
  'goal',
  'foodPreference',
  'macros',
];

/** Key for the reset-password step. Excluded from admin question lists; always shown at end of assessment flow. */
export const PASSWORD_RESET_QUESTION_KEY = 'restPassword';

/**
 * Maps assessment types to their required question keys
 * These keys correspond to the question objects in questionsObj.js
 */
export const ASSESSMENT_QUESTION_MAP = {
  [ASSESSMENT_TYPES.FULL]: [
    'age',                    // Date of birth - required for BMR calculation
    'weight',                 // Current weight - required for BMR calculation
    'height',                 // Height - required for BMR calculation
    'gender',                 // Biological sex - required for BMR formula selection
    'activityLevel',          // Activity level - required for maintenance calorie calculation
    'goal',                   // Primary goal - required for calorie deficit/surplus adjustment
    'foodPreference',         // Food preferences - commonly used for meal plans
    'macros',                 // Macro split preference - required for macro percentages
    'secondaryGoals',         // Additional goals
    'explainReachGoals',      // Explanation of goals
    'trainingActivity',       // Activities outside of training
    'supplements',            // Supplements being taken
    'obstacleReachingFitnessGoals', // Obstacles to reaching goals
    'travel',                 // Travel frequency
    'mealsPrefer',            // Meal preferences
    'happyEatingFoods',       // Foods user enjoys
    'riskFollowing',          // Risk factors following the plan
    'averageStepCount',       // Average daily steps
    'doStrengthTraining',     // Strength training experience
    'weightTraningexprience', // Weight training experience level
    'significantInjuries',    // Significant injuries
    'cardioType',             // Preferred cardio type
    'equipment',              // Available equipment
    'timeForWorkout',         // Time available for workouts
    'workoutFrequency',       // Workout frequency preference
    'currentLocation',        // Current location
    // restPassword excluded: always appended at end of assessment flow
  ],
  [ASSESSMENT_TYPES.BASIC]: [
    'age',           // Required for BMR (Mifflin-St Jeor formula): age factor
    'weight',        // Required for BMR: weight in kg
    'height',        // Required for BMR: height in cm
    'gender',        // Required for BMR formula selection (male/female formula)
    'activityLevel', // Required for maintenance calorie calculation (BMR * activity multiplier)
    'goal',          // Required for calorie deficit/surplus adjustment
    'foodPreference', // Must come before macros - macros options depend on foodPreference
    'macros',        // Required for macro split percentages (protein/carb/fat ratios)
    // restPassword excluded: always appended at end of assessment flow
  ],
  [ASSESSMENT_TYPES.NONE]: [], // No questions required
};

/**
 * Append the reset-password step at the end of the questions object if not already present.
 * Used so restPassword always appears last for full, basic, and custom assessments.
 *
 * @param {Object} questions - Built questions object (keyed by questionKey or custom_*)
 * @param {Object} questionsObj - Full questions object from questionsObj.js
 * @returns {Object} questions with restPassword appended when applicable
 */
function appendPasswordResetStep(questions, questionsObj) {
  if (!questionsObj || !questionsObj[PASSWORD_RESET_QUESTION_KEY]) {
    return questions;
  }
  if (questions[PASSWORD_RESET_QUESTION_KEY]) {
    return questions;
  }
  // Only append when there is at least one other question
  if (Object.keys(questions).length === 0) {
    return questions;
  }
  const maxKey = Math.max(
    0,
    ...Object.values(questions).map((q) => (q && typeof q.key === 'number' ? q.key : 0))
  );
  const nextKey = maxKey + 1;
  return {
    ...questions,
    [PASSWORD_RESET_QUESTION_KEY]: {
      ...questionsObj[PASSWORD_RESET_QUESTION_KEY],
      key: nextKey,
    },
  };
}

/**
 * Build questions object from API assessment response (system or custom).
 * Used when the web app loads assessments from the database.
 *
 * @param {Object} apiAssessment - Assessment from GET /api/assessments?type= or GET /api/assessments/:id
 * @param {Object} questionsObj - The full questions object from questionsObj.js
 * @returns {Object} Questions object keyed by questionKey with sequential key
 */
export const buildQuestionsFromApiAssessment = (apiAssessment, questionsObj) => {
  if (!apiAssessment || !Array.isArray(apiAssessment.questions)) {
    return {};
  }

  const questions = {};
  apiAssessment.questions.forEach((q, index) => {
    const key = index + 1;
    if (q.questionKey && questionsObj && questionsObj[q.questionKey]) {
      questions[q.questionKey] = {
        ...questionsObj[q.questionKey],
        key,
      };
    } else if (q.question && q.questionType) {
      // Prefer the stable slug questionKey assigned at save time; fall back to
      // the legacy array-index key for questions created before slugs existed.
      const fieldKey = q.questionKey || `custom_${index}`;
      const apiOptions = (q.options || []).map((opt) => ({
        value: opt.value,
        label: opt.label || opt.value,
      }));
      const normalizedOptions = normalizeOptionsForComponent(apiOptions);
      const questionType = q.questionType;
      const config = CUSTOM_QUESTION_TYPE_TO_COMPONENT[questionType];

      if (!config) {
        console.warn(`Unknown custom question type: ${questionType}`);
      } else if (config.componentType === 'OptionsOnly') {
        // single_select: no component, options on question (rendered via options-only path)
        questions[fieldKey] = {
          key,
          question: q.question,
          type: questionType,
          custom: true,
          options: normalizedOptions,
          validate: () => null,
          button: true,
        };
      } else {
        const component = buildComponentFromCustom(
          { type: questionType, question: q.question, options: normalizedOptions },
          fieldKey
        );
        questions[fieldKey] = {
          key,
          question: q.question,
          type: questionType,
          custom: true,
          component: component,
          button: true,
        };
      }
    } else if (q.questionKey) {
      console.warn(
        `Question key "${q.questionKey}" from API not found in questionsObj`
      );
    }
  });

  return questions;
};

/**
 * Get total question count from API assessment.
 *
 * @param {Object|null} apiAssessment - Assessment from API
 * @returns {number}
 */
export const getTotalQuestionsFromApi = (apiAssessment) => {
  return apiAssessment?.questions?.length ?? 0;
};

/**
 * Get filtered questions object for a specific assessment type
 *
 * @param {string} assessmentType - The assessment type ('full', 'basic', 'none', or 'custom:{trainerId}')
 * @param {Object} questionsObj - The full questions object from questionsObj.js
 * @param {Object|null} customAssessment - Custom assessment definition (for future use)
 * @returns {Object} Filtered questions object with sequential keys
 */
// The two nutrition-only questions in the mandatory set. Dropped for a client
// whose trainer disabled nutrition (getQuestionsForType's `nutritionOn` arg).
// The server already strips these from CUSTOM assessments per-client; this is
// the client-side belt-and-suspenders that also covers the hardcoded Full/Basic
// presets for the edge case of a client switched off mid-onboarding.
const NUTRITION_ONLY_QUESTION_KEYS = ['foodPreference', 'macros'];

// Remove the nutrition-only questions and re-sequence the numeric ordering
// keys so there are no gaps (the renderer steps through them by `key`).
const dropNutritionQuestions = (questions) => {
  const resequenced = {};
  Object.entries(questions)
    .filter(([qk]) => !NUTRITION_ONLY_QUESTION_KEYS.includes(qk))
    .sort((a, b) => ((a[1] && a[1].key) || 0) - ((b[1] && b[1].key) || 0))
    .forEach(([qk, q], i) => {
      resequenced[qk] = { ...q, key: i + 1 };
    });
  return resequenced;
};

export const getQuestionsForType = (
  assessmentType,
  questionsObj,
  customAssessment = null,
  assessmentFromApi = null,
  nutritionOn = true
) => {
  const applyNutritionFilter = (questions) =>
    nutritionOn ? questions : dropNutritionQuestions(questions);

  // Prefer API assessment when provided (database as source of truth)
  if (assessmentFromApi) {
    const built = applyNutritionFilter(
      buildQuestionsFromApiAssessment(assessmentFromApi, questionsObj)
    );
    if (assessmentType === ASSESSMENT_TYPES.NONE) return built;
    return appendPasswordResetStep(built, questionsObj);
  }
  // Handle custom assessments (future implementation)
  if (assessmentType && assessmentType.startsWith('custom:')) {
    if (!customAssessment) {
      console.warn(
        'Custom assessment requested but not provided, falling back to full'
      );
      return getQuestionsForType(
        ASSESSMENT_TYPES.FULL,
        questionsObj,
        null,
        null,
        nutritionOn
      );
    }
    const built = applyNutritionFilter(
      buildQuestionsFromCustom(customAssessment, questionsObj)
    );
    return appendPasswordResetStep(built, questionsObj);
  }

  // Handle 'none' type - return empty object
  if (assessmentType === ASSESSMENT_TYPES.NONE) {
    return {};
  }

  // Get question keys for the assessment type, default to full if type not
  // found. Drop the nutrition-only keys up front when nutrition is off.
  const questionKeys = (
    ASSESSMENT_QUESTION_MAP[assessmentType] ||
    ASSESSMENT_QUESTION_MAP[ASSESSMENT_TYPES.FULL]
  ).filter((key) => nutritionOn || !NUTRITION_ONLY_QUESTION_KEYS.includes(key));

  // Filter and reassign sequential keys
  const filtered = {};
  questionKeys.forEach((key, index) => {
    if (questionsObj && questionsObj[key]) {
      filtered[key] = {
        ...questionsObj[key],
        key: index + 1, // Reassign sequential keys starting from 1
      };
    } else {
      console.warn(`Question key "${key}" not found in questionsObj`);
    }
  });

  return appendPasswordResetStep(filtered, questionsObj);
};

/**
 * Get total number of questions for an assessment type
 *
 * @param {string} assessmentType - The assessment type
 * @param {Object|null} customAssessment - Custom assessment definition (for future use)
 * @returns {number} Total number of questions
 */
export const getTotalQuestionsForType = (
  assessmentType,
  customAssessment = null,
  assessmentFromApi = null
) => {
  if (assessmentFromApi) {
    return getTotalQuestionsFromApi(assessmentFromApi);
  }
  // Handle custom assessments (future implementation)
  if (assessmentType && assessmentType.startsWith('custom:')) {
    return customAssessment?.questions?.length || 0;
  }

  // Handle 'none' type
  if (assessmentType === ASSESSMENT_TYPES.NONE) {
    return 0;
  }

  // Return count for system assessment types
  return (
    ASSESSMENT_QUESTION_MAP[assessmentType]?.length ||
    ASSESSMENT_QUESTION_MAP[ASSESSMENT_TYPES.FULL].length
  );
};

/**
 * Check if an assessment type requires the user to complete an assessment
 *
 * @param {string} assessmentType - The assessment type
 * @returns {boolean} True if assessment is required, false if not
 */
export const requiresAssessment = (assessmentType) => {
  return assessmentType !== ASSESSMENT_TYPES.NONE;
};

/**
 * Check if an assessment type is a custom assessment
 *
 * @param {string} assessmentType - The assessment type
 * @returns {boolean} True if it's a custom assessment type
 */
export const isCustomAssessment = (assessmentType) => {
  return assessmentType && assessmentType.startsWith('custom:');
};

/**
 * Extract trainer ID from custom assessment type string
 *
 * @param {string} assessmentType - Custom assessment type (format: 'custom:{trainerId}')
 * @returns {string} Trainer ID
 */
export const getTrainerIdFromCustomType = (assessmentType) => {
  if (!isCustomAssessment(assessmentType)) {
    return null;
  }
  return assessmentType.replace('custom:', '');
};

/**
 * Build questions object from custom assessment definition (trainer-created assessments).
 *
 * @param {Object} customAssessment - Custom assessment definition
 * @param {Object} questionsObj - The full questions object from questionsObj.js
 * @returns {Object} Questions object built from custom assessment
 */
const buildQuestionsFromCustom = (customAssessment, questionsObj) => {
  if (!customAssessment || !customAssessment.questions) {
    console.warn('Invalid custom assessment definition');
    return {};
  }

  const questions = {};
  customAssessment.questions.forEach((customQ, index) => {
    const fieldKey = `custom_${index}`;
    if (customQ.questionKey && questionsObj[customQ.questionKey]) {
      questions[customQ.questionKey] = {
        ...questionsObj[customQ.questionKey],
        key: index + 1,
      };
    } else if (customQ.customQuestion) {
      const cq = customQ.customQuestion;
      const options = normalizeOptionsForComponent(cq.options || []);
      const config = CUSTOM_QUESTION_TYPE_TO_COMPONENT[cq.type];
      if (config && config.componentType === 'OptionsOnly') {
        questions[`custom_${index}`] = {
          key: index + 1,
          question: cq.question,
          type: cq.type,
          options,
          validate: () => null,
          button: true,
        };
      } else {
        questions[`custom_${index}`] = {
          key: index + 1,
          question: cq.question,
          type: cq.type,
          component: buildComponentFromCustom(
            { type: cq.type, question: cq.question, options },
            fieldKey
          ),
          button: true,
        };
      }
    }
  });

  return questions;
};

/**
 * Build component structure from custom question definition.
 * Uses CUSTOM_QUESTION_TYPE_TO_COMPONENT so custom types render as the correct UI.
 *
 * @param {Object} customQuestion - { type, question, options } (options already normalized to { key, text, value }[])
 * @param {string} fieldKey - e.g. 'custom_0' for responseObj key
 * @returns {Array|null} Component array for AssessmentQuestions, or null for OptionsOnly (caller sets question.options)
 */
function buildComponentFromCustom(customQuestion, fieldKey) {
  const { type, question, options = [] } = customQuestion;
  const config = CUSTOM_QUESTION_TYPE_TO_COMPONENT[type];
  if (!config) return [{ type: 'Input', render: [{ value: fieldKey, type: question || 'Type here...', validate: () => null }] }];

  const defaultValidate = () => null;

  switch (config.componentType) {
    case 'Input': {
      const { inputType = 'text', placeholder } = config.defaults;
      return [
        {
          type: 'Input',
          render: [
            {
              value: fieldKey,
              type: question || placeholder,
              inputType,
              active: true,
              validate: defaultValidate,
            },
          ],
        },
      ];
    }
    case 'TextArea': {
      const { placeholder } = config.defaults;
      return [
        {
          type: 'TextArea',
          render: [
            {
              value: fieldKey,
              type: placeholder,
              active: true,
              validate: defaultValidate,
            },
          ],
        },
      ];
    }
    case 'CustomCheckbox': {
      const hasOther = options.some((o) => o && String(o.value) === 'Other');
      const validate = hasOther
        ? (selectedOptions, otherDescription) => {
            if (
              selectedOptions &&
              selectedOptions.includes('Other') &&
              !(otherDescription && String(otherDescription).trim())
            ) {
              return 'Please describe Other';
            }
            return null;
          }
        : defaultValidate;
      return [
        {
          type: 'CustomCheckbox',
          render: [
            {
              value: fieldKey,
              options: options.length ? options : [{ key: '1', text: 'Option 1', value: 'option1' }],
              validate,
            },
          ],
        },
      ];
    }
    case 'DatePicker': {
      return [
        {
          type: 'DatePicker',
          render: [{ value: fieldKey, validate: defaultValidate }],
        },
      ];
    }
    case 'DatePickerBirthday': {
      return [
        {
          type: 'DatePickerBirthday',
          render: [{ value: fieldKey, validate: defaultValidate }],
        },
      ];
    }
    default:
      return [{ type: 'Input', render: [{ value: fieldKey, type: question || 'Type here...', validate: defaultValidate }] }];
  }
}

/**
 * Build a single custom question shape for preview (e.g. in Create assessment modal).
 * Same shape as activeQuestion so it can be rendered by the same component or a preview-only renderer.
 *
 * @param {Object} row - { question, questionType, options? } (options: [{ value, label }])
 * @param {number} index - Row index (0-based)
 * @returns {Object} Question object { key, question, type, component?, options?, validate, button }
 */
export function buildPreviewQuestionFromRow(row, index) {
  const fieldKey = `preview_${index}`;
  const apiOptions = (row.options || []).map((o) => ({ value: o.value || o.label, label: o.label || o.value }));
  let normalizedOptions = normalizeOptionsForComponent(apiOptions);
  const questionType = row.questionType || 'text';
  const config = CUSTOM_QUESTION_TYPE_TO_COMPONENT[questionType];

  if (!config) {
    return {
      key: index + 1,
      question: row.question || 'Question text',
      type: questionType,
      component: buildComponentFromCustom(
        { type: questionType, question: row.question, options: [] },
        fieldKey
      ),
      validate: () => null,
      button: true,
    };
  }

  if (config.componentType === 'OptionsOnly') {
    if (normalizedOptions.length === 0) {
      normalizedOptions = [
        { key: '1', text: 'Option 1', value: 'option1' },
        { key: '2', text: 'Option 2', value: 'option2' },
      ];
    }
    return {
      key: index + 1,
      question: row.question || 'Question text',
      type: questionType,
      options: normalizedOptions,
      validate: () => null,
      button: true,
    };
  }

  if (config.componentType === 'CustomCheckbox' && normalizedOptions.length === 0) {
    normalizedOptions = [
      { key: '1', text: 'Option 1', value: 'option1' },
      { key: '2', text: 'Option 2', value: 'option2' },
    ];
  }

  return {
    key: index + 1,
    question: row.question || 'Question text',
    type: questionType,
    component: buildComponentFromCustom(
      { type: questionType, question: row.question, options: normalizedOptions },
      fieldKey
    ),
    validate: () => null,
    button: true,
  };
}
